import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from '@store';
import {
  CapaQuotationUserType,
  useFetchQuotationChatMessagesQuery,
  useFetchQuotationUserRfqDetailQuery,
  useReadQuotationChatMessageMutation,
  useSubscribeQuotationChatMessageSubscription
} from '@generated/graphql';
import ChatMessagesViewPresenter from './ChatMessagesViewPresenter';
import useChatMessages from '../useChatMessages';

interface Props {
  matches: boolean;
}

const ChatMessageContainer = ({ matches }: Props) => {
  const rfqId = useSelector((state) => state.chat.rfqId ?? '');
  const {
    chatMessages: subscribeMessages,
    onUpdateChatMessages,
    onInitChatMessages
  } = useChatMessages();

  const { chatRoomId } = useParams() as { chatRoomId: string };

  const { data: fetchRfqDetail } = useFetchQuotationUserRfqDetailQuery({
    variables: {
      rfqId: rfqId ?? ''
    },
    skip: !rfqId,
    fetchPolicy: 'cache-first'
  });

  const { data: fetchMessages, loading } = useFetchQuotationChatMessagesQuery({
    variables: {
      input: {
        chatRoomId: chatRoomId ?? ''
      }
    },
    fetchPolicy: 'network-only',
    skip: !chatRoomId
  });

  const [readQuotationChatMessage] = useReadQuotationChatMessageMutation({
    variables: {
      chatRoomId: chatRoomId ?? ''
    }
  });

  useSubscribeQuotationChatMessageSubscription({
    variables: {
      input: {
        chatRoomId: chatRoomId ?? ''
      }
    },
    onSubscriptionData(options) {
      const chatMessage = options.subscriptionData.data
        ?.subscribeQuotationChatMessage.data![0];

      chatMessage && onUpdateChatMessages(chatMessage);

      if (chatMessage?.senderUserType === CapaQuotationUserType.Partner) {
        readQuotationChatMessage();
      }
    }
  });

  useEffect(() => {
    if (chatRoomId) {
      readQuotationChatMessage();
    }

    return () => {
      onInitChatMessages();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatRoomId]);

  const rfqDetail =
    fetchRfqDetail?.fetchQuotationUserRfqDetail.data ?? undefined;

  const messages = fetchMessages?.fetchQuotationChatMessages.data ?? [];

  return (
    <ChatMessagesViewPresenter
      matches={matches}
      loading={loading || !rfqDetail}
      chatRoomId={chatRoomId ?? ''}
      messages={messages}
      rfqDetail={rfqDetail}
      subscribeMessages={subscribeMessages}
    />
  );
};

export default ChatMessageContainer;
